import React, { useState, useEffect } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { advancedBillingService } from '../../../services/advancedBillingService';
import { getTranslation } from '../../../utils/translations';

const RegionManagementModal = ({ isOpen, onClose, language }) => {
  const t = getTranslation(language);
  const [loading, setLoading] = useState(false);
  const [regions, setRegions] = useState([]);
  const [error, setError] = useState(null);
  const [editingRegion, setEditingRegion] = useState(null);
  const [formData, setFormData] = useState({
    regionCode: '',
    regionName: '',
    description: '',
    isActive: true
  });

  useEffect(() => {
    if (isOpen) {
      fetchRegions();
    }
  }, [isOpen]);

  const fetchRegions = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('RegionManagementModal: Starting to fetch regions...');
      const data = await advancedBillingService?.getAllRegions();
      console.log('RegionManagementModal: Received data:', data);
      setRegions(data || []);
      if (!data || data?.length === 0) {
        console.warn('RegionManagementModal: No regions returned from service');
      }
    } catch (err) {
      console.error('RegionManagementModal: Error fetching regions:', err);
      setError(err?.message || 'Unknown error occurred');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setError(null);
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    
    if (!formData?.regionCode || !formData?.regionName) {
      setError(language === 'FR' ? 'Veuillez remplir tous les champs obligatoires' : 
               language === 'EN' ? 'Please fill in all required fields' : 'Proszę wypełnić wszystkie wymagane pola');
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      if (editingRegion) {
        await advancedBillingService?.updateRegion(editingRegion?.id, formData);
      } else {
        await advancedBillingService?.createRegion(formData);
      }
      
      await fetchRegions();
      resetForm();
    } catch (err) {
      console.error('Error saving region:', err);
      setError(err?.message || (language === 'FR' ? 'Erreur lors de la sauvegarde' : 
                                 language === 'EN' ? 'Error saving region' : 'Błąd podczas zapisywania'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (region) => {
    setEditingRegion(region);
    setFormData({
      regionCode: region?.regionCode,
      regionName: region?.regionName,
      description: region?.description || '',
      isActive: region?.isActive
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm(language === 'FR' ? 'Êtes-vous sûr de vouloir supprimer cette région ?' : 
                        language === 'EN' ? 'Are you sure you want to delete this region?' : 
                        'Czy na pewno chcesz usunąć ten region?')) {
      return;
    }

    try {
      setLoading(true);
      await advancedBillingService?.deleteRegion(id);
      await fetchRegions();
    } catch (err) {
      console.error('Error deleting region:', err);
      setError(err?.message);
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      regionCode: '',
      regionName: '',
      description: '',
      isActive: true
    });
    setEditingRegion(null);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-card rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-card border-b border-border p-6 flex items-center justify-between">
          <h2 className="text-2xl font-bold text-foreground">
            {language === 'FR' ? 'Gestion des Régions' : language === 'EN' ? 'Region Management' : 'Zarządzanie Regionami'}
          </h2>
          <button
            onClick={onClose}
            className="text-muted-foreground hover:text-foreground transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="bg-destructive/10 border border-destructive text-destructive px-4 py-3 rounded-lg flex items-center gap-2">
              <Icon name="AlertCircle" size={20} />
              <span>{error}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="bg-muted/30 p-4 rounded-lg space-y-4">
            <h3 className="text-lg font-semibold text-foreground">
              {editingRegion ? 
                (language === 'FR' ? 'Modifier la Région' : language === 'EN' ? 'Edit Region' : 'Edytuj Region') :
                (language === 'FR' ? 'Ajouter une Région' : language === 'EN' ? 'Add Region' : 'Dodaj Region')
              }
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'FR' ? 'Code Région' : language === 'EN' ? 'Region Code' : 'Kod Regionu'} *
                </label>
                <Input
                  type="text"
                  value={formData?.regionCode}
                  onChange={(e) => handleInputChange('regionCode', e?.target?.value?.toUpperCase())}
                  placeholder="MAGHREB, WEST_EU"
                  disabled={!!editingRegion}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  {language === 'FR' ? 'Nom de la Région' : language === 'EN' ? 'Region Name' : 'Nazwa Regionu'} *
                </label>
                <Input
                  type="text"
                  value={formData?.regionName}
                  onChange={(e) => handleInputChange('regionName', e?.target?.value)}
                  placeholder={language === 'FR' ? 'Maghreb' : language === 'EN' ? 'Maghreb' : 'Maghreb'}
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {language === 'FR' ? 'Description' : language === 'EN' ? 'Description' : 'Opis'}
              </label>
              <Input
                type="text"
                value={formData?.description}
                onChange={(e) => handleInputChange('description', e?.target?.value)}
                placeholder={language === 'FR' ? 'Description de la région' : language === 'EN' ? 'Region description' : 'Opis regionu'}
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                type="checkbox"
                id="regionActive"
                checked={formData?.isActive}
                onChange={(e) => handleInputChange('isActive', e?.target?.checked)}
                className="rounded border-border text-primary focus:ring-primary"
              />
              <label htmlFor="regionActive" className="text-sm font-medium text-foreground cursor-pointer">
                {language === 'FR' ? 'Actif' : language === 'EN' ? 'Active' : 'Aktywny'}
              </label>
            </div>

            <div className="flex items-center gap-3">
              <Button type="submit" variant="primary" disabled={loading}>
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <>
                    <Icon name={editingRegion ? "Save" : "Plus"} size={16} className="mr-2" />
                    {editingRegion ? 
                      (language === 'FR' ? 'Mettre à jour' : language === 'EN' ? 'Update' : 'Aktualizuj') :
                      (language === 'FR' ? 'Ajouter' : language === 'EN' ? 'Add' : 'Dodaj')
                    }
                  </>
                )}
              </Button>
              {editingRegion && (
                <Button type="button" variant="outline" onClick={resetForm}>
                  {language === 'FR' ? 'Annuler' : language === 'EN' ? 'Cancel' : 'Anuluj'}
                </Button>
              )}
            </div>
          </form>

          <div className="space-y-3">
            <h3 className="text-lg font-semibold text-foreground">
              {language === 'FR' ? 'Régions Existantes' : language === 'EN' ? 'Existing Regions' : 'Istniejące Regiony'}
            </h3>
            
            {loading && regions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {language === 'FR' ? 'Chargement...' : language === 'EN' ? 'Loading...' : 'Ładowanie...'}
              </div>
            ) : regions?.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {language === 'FR' ? 'Aucune région trouvée' : language === 'EN' ? 'No regions found' : 'Nie znaleziono regionów'}
              </div>
            ) : (
              <div className="grid grid-cols-1 gap-3">
                {regions?.map((region) => (
                  <div key={region?.id} className="bg-background border border-border rounded-lg p-4 flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <h4 className="font-semibold text-foreground">{region?.regionName}</h4>
                        <span className="text-xs bg-muted px-2 py-1 rounded">{region?.regionCode}</span>
                        {region?.isActive && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            {language === 'FR' ? 'Actif' : language === 'EN' ? 'Active' : 'Aktywny'}
                          </span>
                        )}
                      </div>
                      {region?.description && (
                        <p className="text-sm text-muted-foreground mt-1">{region?.description}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleEdit(region)}
                        className="p-2 text-primary hover:bg-primary/10 rounded transition-colors"
                        title={language === 'FR' ? 'Modifier' : language === 'EN' ? 'Edit' : 'Edytuj'}
                      >
                        <Icon name="Edit" size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(region?.id)}
                        className="p-2 text-destructive hover:bg-destructive/10 rounded transition-colors"
                        title={language === 'FR' ? 'Supprimer' : language === 'EN' ? 'Delete' : 'Usuń'}
                      >
                        <Icon name="Trash2" size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default RegionManagementModal;